import type { ReportColumn } from '../models/column.types'
import { MATCH_MODES, type FilterMatchMode } from '../models/filter.types'

import { isNumericReportColumn } from './reportColumnKind'

export type ReportFilterModeValueShape = 'single' | 'multiple' | 'range' | 'none'
export type ReportFilterModeLabelKey = `reports.filters.matchModes.${string}`

export interface ReportFilterModeDefinition {
  readonly value: FilterMatchMode
  readonly labelKey: ReportFilterModeLabelKey
  readonly valueShape: ReportFilterModeValueShape
}

type FilterModeColumn = Pick<
  ReportColumn,
  'currency' | 'invisible' | 'isImage' | 'numeric' | 'percentage' | 'sub_type'
>

const LABEL_SUFFIX_BY_MODE = {
  equals: 'equals',
  not_equals: 'notEquals',
  in: 'in',
  not_in: 'notIn',
  contains: 'contains',
  not_contains: 'notContains',
  starts_with: 'startsWith',
  ends_with: 'endsWith',
  greater_than: 'greaterThan',
  greater_than_or_equal_to: 'greaterThanOrEqualTo',
  less_than: 'lessThan',
  less_than_or_equal_to: 'lessThanOrEqualTo',
  between: 'between',
  regex: 'regex',
  not_regex: 'notRegex',
  is_empty: 'isEmpty',
  is_not_empty: 'isNotEmpty',
} as const satisfies Readonly<Record<FilterMatchMode, string>>

const VALUE_SHAPE_BY_MODE = {
  equals: 'single',
  not_equals: 'single',
  in: 'multiple',
  not_in: 'multiple',
  contains: 'single',
  not_contains: 'single',
  starts_with: 'single',
  ends_with: 'single',
  greater_than: 'single',
  greater_than_or_equal_to: 'single',
  less_than: 'single',
  less_than_or_equal_to: 'single',
  between: 'range',
  regex: 'single',
  not_regex: 'single',
  is_empty: 'none',
  is_not_empty: 'none',
} as const satisfies Readonly<Record<FilterMatchMode, ReportFilterModeValueShape>>

const TEXT_FILTER_MODES = new Set<FilterMatchMode>([
  'equals',
  'not_equals',
  'in',
  'not_in',
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
  'regex',
  'not_regex',
  'is_empty',
  'is_not_empty',
])

const ARRAY_FILTER_MODES = new Set<FilterMatchMode>([
  'equals',
  'not_equals',
  'in',
  'not_in',
  'contains',
  'not_contains',
  'is_empty',
  'is_not_empty',
])

const NUMERIC_FILTER_MODES = new Set<FilterMatchMode>([
  'equals',
  'not_equals',
  'in',
  'not_in',
  'greater_than',
  'greater_than_or_equal_to',
  'less_than',
  'less_than_or_equal_to',
  'between',
  'is_empty',
  'is_not_empty',
])

export const REPORT_FILTER_MODE_DEFINITIONS: readonly ReportFilterModeDefinition[] =
  MATCH_MODES.map((value) => ({
    value,
    labelKey: `reports.filters.matchModes.${LABEL_SUFFIX_BY_MODE[value]}`,
    valueShape: VALUE_SHAPE_BY_MODE[value],
  }))

function availableModeSet(column: FilterModeColumn): ReadonlySet<FilterMatchMode> {
  if (column.sub_type === 'array') return ARRAY_FILTER_MODES
  return isNumericReportColumn(column) ? NUMERIC_FILTER_MODES : TEXT_FILTER_MODES
}

/** Return the canonical label/value contract for one persisted match mode. */
export function getReportFilterModeDefinition(mode: FilterMatchMode): ReportFilterModeDefinition {
  const definition = REPORT_FILTER_MODE_DEFINITIONS.find((candidate) => candidate.value === mode)
  if (!definition) throw new Error(`Unknown report filter match mode: ${String(mode)}`)
  return definition
}

/** Intersect caller policy with modes that the selected column can evaluate meaningfully. */
export function getAvailableReportFilterModes(
  column: FilterModeColumn | null | undefined,
  allowedModes: readonly FilterMatchMode[] = MATCH_MODES,
): FilterMatchMode[] {
  if (
    !column ||
    column.invisible === true ||
    column.isImage === true ||
    column.sub_type === 'image'
  )
    return []

  const available = availableModeSet(column)
  const seen = new Set<FilterMatchMode>()
  return allowedModes.filter((mode) => {
    if (!available.has(mode) || seen.has(mode)) return false
    seen.add(mode)
    return true
  })
}

/** Choose the legacy-compatible default, falling back to the first allowed compatible mode. */
export function getDefaultReportFilterMode(
  column: FilterModeColumn | null | undefined,
  allowedModes: readonly FilterMatchMode[] = MATCH_MODES,
): FilterMatchMode | null {
  const available = getAvailableReportFilterModes(column, allowedModes)
  if (!column || available.length === 0) return null

  const preferred = isNumericReportColumn(column)
    ? 'greater_than'
    : column.sub_type === 'array'
      ? 'in'
      : 'contains'
  return available.includes(preferred) ? preferred : (available[0] ?? null)
}
